/**
 * Bug'latgich — dinamik rejim (kamera konsentratsiyasi va massasi).
 *
 *   dM/dt      = F − L − W
 *   d(M·x)/dt  = F·x_F − L·x
 *   W          — oniy issiqlik balansidan (steady.ts `instantBalance`, joriy x bilan)
 *   L          = max(0, F − W + (M − M_set)/τ_L)   — sath rostlagichi (proporsional)
 *
 * ASSUMPTION: suyuqlik doim qaynash haroratida (issiqlik inersiyasi e'tiborsiz; sath
 * dinamikasi konsentratsiya dinamikasidan ancha sekin bo'lgani uchun yetarli).
 */
import { warn, type StepResult, type Warning } from '../../types';
import { instantBalance, validateEvaporatorInputs, validateEvaporatorParams } from './steady';
import type { EvaporatorInputs, EvaporatorOutputs, EvaporatorParams, EvaporatorState } from './types';

export const DEFAULT_HOLDUP = 1000;
export const DEFAULT_LEVEL_TAU = 60;

export function initState(p: EvaporatorParams, i: EvaporatorInputs): EvaporatorState {
  validateEvaporatorParams(p);
  validateEvaporatorInputs(i);
  return { t: 0, M: p.holdup ?? DEFAULT_HOLDUP, x: i.feedFraction };
}

export function stepDynamic(p: EvaporatorParams, s: EvaporatorState, i: EvaporatorInputs, dt: number): StepResult<EvaporatorState, EvaporatorOutputs> {
  validateEvaporatorInputs(i);
  if (!(dt > 0)) throw new Error('evaporator: dt must be > 0');
  const warnings: Warning[] = [];
  const Mset = p.holdup ?? DEFAULT_HOLDUP;
  const tau = p.levelTau ?? DEFAULT_LEVEL_TAU;
  const F = i.feedFlow;
  const b = instantBalance(p, i, s.x, warnings);
  let W = b.W;
  // bug'lanish kameradagi erituvchidan oshmasin
  const solventMass = s.M * (1 - s.x);
  W = Math.min(W, Math.max(0, solventMass / dt) * 0.5);
  const L = Math.max(0, F - W + (s.M - Mset) / tau);
  const M = Math.max(1e-6, s.M + (F - L - W) * dt);
  const solute = Math.max(0, s.M * s.x + (F * i.feedFraction - L * s.x) * dt);
  const x = Math.min(0.999, solute / M);
  if (b.deltaT <= 0) warn(warnings, 'evap.noEvaporation', 'critical', { Tsteam: Math.round(b.Tsteam), Tb: Math.round(b.Tb) });
  else if (b.deltaT < 5) warn(warnings, 'evap.lowDeltaT', 'info', { deltaT: Math.round(b.deltaT * 10) / 10 });
  if (x > p.xMax) warn(warnings, 'evap.crystallization', 'warning', { xL: Math.round(x * 1000) / 1000, xMax: p.xMax });
  if (x >= 0.99) warn(warnings, 'evap.dryOut', 'critical');
  const D = b.Q / b.rs;
  const outputs: EvaporatorOutputs = {
    t: s.t + dt,
    W,
    L,
    xL: s.x,
    Tb: b.Tb,
    Tsat: b.Tsat,
    bpe: b.bpe,
    Tsteam: b.Tsteam,
    deltaT: b.deltaT,
    Q: b.Q,
    D,
    economy: D > 0 ? W / D : 0,
    specificSteam: W > 0 ? D / W : Infinity,
    r: b.r,
    M,
  };
  return { state: { t: s.t + dt, M, x }, outputs, warnings };
}
